import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { ExpenseService, GroupedExpense } from '../../core/services/expense.service';
import { AuthService } from '../../core/services/auth.service';
import { TripService } from '../../core/services/trip.service';
import { UserService } from '../../core/services/user.service';
import { FamilyService } from '../../core/services/family.service';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { ToastComponent } from '../../shared/components/toast/toast.component';
import { TripMembersDialogComponent } from '../trips/trip-members-dialog.component';
import { TripExpenseEntryDialogComponent } from '../trips/trip-expense-entry-dialog/trip-expense-entry-dialog.component';
import { ExpenseEntryDialogComponent } from './expense-entry-dialog.component';

@Component({
    selector: 'app-home',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatDatepickerModule,
        MatProgressBarModule,
        MatDialogModule,
        MatSnackBarModule
    ],
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css']
})
/**
 * === TRANSACTION LIST COMPONENT (Routes: /family, /trips/:id) ===
 * Functionality:
 *  - Displays a list of expenses for a specific context.
 *  - Context #1: FAMILY (Route: /family) -> Shows all family expenses.
 *  - Context #2: TRIP (Route: /trips/:id) -> Shows expenses for a specific trip.
 *  - Handles "Swipe to Edit/Delete" interactions.
 *  - Navigation Hub for "Add Expense".
 */
export class HomeComponent implements OnInit {
    expenseService = inject(ExpenseService);
    authService = inject(AuthService);
    tripService = inject(TripService);
    userService = inject(UserService);
    familyService = inject(FamilyService);
    router = inject(Router);
    route = inject(ActivatedRoute);
    cd = inject(ChangeDetectorRef);
    dialog = inject(MatDialog);
    snackBar = inject(MatSnackBar);

    // Form
    categories$ = this.expenseService.categories$;

    // UI Helpers (Matches Dashboard)
    colors = ['#6C5DD3', '#FF708B', '#FFBA69', '#01C0C8', '#8A76FF', '#FF8F6B', '#05CD99', '#A3AED0'];

    getIconForCategory(cat: string): string {
        const map: { [key: string]: string } = {
            'Food': 'restaurant',
            'Travel': 'flight',
            'Entertainment': 'movie',
            'Shopping': 'shopping_bag',
            'Education': 'school',
            'Health': 'medical_services',
            'Bills': 'receipt_long',
            'Utilities': 'bolt',
            'Rent': 'home',
            'Loan': 'account_balance',
            'Investment': 'trending_up',
            'Savings': 'savings',
            'Transport': 'directions_car',
            'Groceries': 'local_grocery_store',
            'Dining': 'restaurant_menu',
            'Others': 'more_horiz',
            'Other': 'more_horiz'
        };
        const key = Object.keys(map).find(k => cat.toLowerCase().includes(k.toLowerCase()));
        return key ? map[key] : 'category';
    }

    getColorForCategory(cat: string): string {
        // Simple distinct hashing for consistent colors
        let hash = 0;
        for (let i = 0; i < cat.length; i++) {
            hash = cat.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % this.colors.length;
        return this.colors[index];
    }

    // View State
    date: string = '';
    currentDayGroup: GroupedExpense | null = null;
    contextTitle: string = 'Expenses';
    currentTripId: string | null = null;
    currentTripStatus: 'active' | 'completed' = 'active';
    tripStartDate: Date | null = null;
    tripEndDate: Date | null = null;

    // Swipe tracking
    private swipeStartX: number = 0;
    private swipeStartTime: number = 0;

    constructor() {
        // Initialize date from Service State if available (preserves navigation context)
        const currentServiceDate = this.expenseService.getCurrentDateValue();
        if (currentServiceDate) {
            this.date = currentServiceDate;
        } else {
            this.date = this.expenseService.getToday();
        }
    }

    // State
    membersMap: { [uid: string]: string } = {};

    ngOnInit() {
        /**
         * @description Robust Navigation Handler
         */
        combineLatest([
            this.route.url,
            this.route.paramMap,
            this.tripService.trips$
        ]).subscribe(async ([segments, params, trips]) => {
            if (segments.length === 0) return;
            const path = segments[0] ? segments[0].path : '';

            if (path === 'family') {
                this.currentTripId = null;
                this.expenseService.setContext('family', 'default');

                this.familyService.currentFamily$.subscribe(async family => {
                    this.contextTitle = family ? `${family.name} Expenses` : 'Family Expenses';

                    // Always add current user to membersMap
                    const currentUser = this.authService.getCurrentUser();
                    if (currentUser && !this.membersMap[currentUser.uid]) {
                        this.membersMap[currentUser.uid] = currentUser.displayName || 'You';
                    }

                    if (family && family.members) {
                        const memberIds = Array.isArray(family.members) ? family.members : [];

                        await Promise.all(memberIds.map(async uid => {
                            if (!this.membersMap[uid]) {
                                const p = await this.userService.getProfile(uid);
                                if (p) this.membersMap[uid] = p.displayName || 'User';
                            }
                        }));
                        this.cd.detectChanges(); // Trigger update after fetching
                    }
                });
            }
            else if (path === 'trips') {
                const tripId = params.get('id');
                if (tripId) {
                    this.currentTripId = tripId;
                    this.expenseService.setContext('trip', tripId);

                    const trip = trips.find(t => t.id === tripId);
                    if (trip) {
                        this.contextTitle = trip.name;
                        this.currentTripStatus = trip.status;
                        this.expenseService.setContextStatus(trip.status); // SYNC WITH SERVICE
                        // Load Trip Members
                        if (trip.members) {
                            const memberIds = trip.members;
                            await Promise.all(memberIds.map(async uid => {
                                if (!this.membersMap[uid]) {
                                    const p = await this.userService.getProfile(uid);
                                    if (p) this.membersMap[uid] = p.displayName || 'User';
                                }
                            }));
                            this.cd.detectChanges();
                        }

                        // DATE RESTICTION LOGIC
                        this.tripStartDate = trip.startDate ? new Date(trip.startDate) : null;
                        this.tripEndDate = trip.endDate ? new Date(trip.endDate) : null;

                        this.validateAndAdjustDate();
                    }
                }
            }
            this.cd.detectChanges();
        });

        // Subscribe to expenses
        this.expenseService.groupedExpenses$.subscribe(groups => {
            this.updateCurrentDayGroup(groups);
            setTimeout(() => this.cd.detectChanges(), 0);
        });
    }

    getUserName(uid: string): string {
        const fullName = this.membersMap[uid] || uid; // Fallback to UID if not found (or 'Unknown')
        return fullName.split(' ')[0];
    }



    openTripMembersDialog() {
        if (!this.currentTripId) return;
        const trip = this.tripService.getTripSnapshot(this.currentTripId);
        if (!trip) return;

        this.dialog.open(TripMembersDialogComponent, {
            data: { trip },
            width: '400px'
        });
    }

    updateCurrentDayGroup(groups: GroupedExpense[]) {
        this.currentDayGroup = groups.find(g => g.date === this.date) || null;
    }

    private toLocalISODate(date: Date): string {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    validateAndAdjustDate() {
        if (!this.currentTripId) return;

        const current = new Date(this.date);

        // check end date first (per user request to see last day)
        if (this.tripEndDate) {
            // Use local date string comparison
            const endStr = this.toLocalISODate(this.tripEndDate);
            const currentStr = this.date; // already YYYY-MM-DD

            if (currentStr > endStr) {
                this.date = endStr;
                this.expenseService.setCurrentDate(this.date);
                this.updateCurrentDayGroup(this.expenseService.getGroupedExpensesValue());
                return;
            }
        }

        if (this.tripStartDate) {
            const startStr = this.toLocalISODate(this.tripStartDate);
            const currentStr = this.date;

            if (currentStr < startStr) {
                this.date = startStr;
                this.expenseService.setCurrentDate(this.date);
                this.updateCurrentDayGroup(this.expenseService.getGroupedExpensesValue());
            }
        }
    }

    changeDate(days: number) {
        const currentDate = new Date(this.date);
        currentDate.setDate(currentDate.getDate() + days);
        // Use helper to ensure we stay local
        const newDateString = this.toLocalISODate(currentDate);

        // CHECK BOUNDS
        if (this.currentTripId) {
            // Using string comparison for YYYY-MM-DD is safe and effective
            if (this.tripStartDate) {
                const startStr = this.toLocalISODate(this.tripStartDate);
                if (newDateString < startStr) return; // Block
            }
            if (this.tripEndDate) {
                const endStr = this.toLocalISODate(this.tripEndDate);
                if (newDateString > endStr) return; // Block
            }
        }

        this.date = newDateString;

        // SYNC GLOBAL DATE STATE
        this.expenseService.setCurrentDate(this.date);
        this.updateCurrentDayGroup(this.expenseService.getGroupedExpensesValue());
    }

    getTotal(): number {
        return this.currentDayGroup?.total || 0;
    }

    openExpenseDialog(expense?: any) {
        if (this.currentTripId) {
            // TRIP CONTEXT
            if (expense) {
                // Edit Trip Expense
                this.router.navigate(['/trips', this.currentTripId, 'edit', expense.id]);
            } else {
                // Add Trip Expense
                this.router.navigate(['/trips', this.currentTripId, 'add']);
            }
        } else {
            // FAMILY CONTEXT
            if (expense) {
                // Edit Family Expense
                this.router.navigate(['/family/edit', expense.id]);
            } else {
                // Add Family Expense
                this.router.navigate(['/family/add']);
            }
        }
    }

    private saveExpense(formData: any, expenseId?: string) {
        this.authService.user$.subscribe(async user => {
            if (user) {
                if (!this.currentTripId) {
                    const profile = await this.userService.getProfile(user.uid);
                    if (!profile || !profile.familyId) {
                        this.showToast('Error', 'Please create or join a family first!', 'error');
                        return;
                    }
                }

                try {
                    if (expenseId) {
                        await this.expenseService.updateExpense(expenseId, formData);
                        this.showToast('Success', 'Expense updated successfully', 'success');
                    } else {
                        await this.expenseService.addExpense({
                            ...formData,
                            user: formData.user || user.uid // Use UID for consistent member mapping
                        });
                        this.showToast('Success', 'Expense added successfully', 'success');
                    }
                } catch (err: any) {
                    this.showToast('Error', err.message || 'Error occurred', 'error');
                }
            }
        });
    }

    async deleteExpense(expense: any) {
        if (!expense || !expense.id) return;

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'k-bottom-sheet-dialog',
        position: { bottom: '0' },
        data: {
            title: 'Delete Expense',
            icon: '🗑️',
            value: (expense.amount || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' }),
            message: `Are you sure you want to delete this expense from ${expense.category}?`,
            warning: 'This action cannot be undone and will update your balance.',
            confirmText: 'Delete',
            type: 'delete'
        }
    });

        dialogRef.afterClosed().subscribe(async result => {
            if (result) {
                try {
                    await this.expenseService.deleteExpense(expense.id);
                    this.showToast('Success', 'Expense deleted successfully', 'success');
                } catch (err: any) {
                    this.showToast('Error', err.message, 'error');
                }
            }
        });
    }

    onSwipeStart(event: TouchEvent | MouseEvent) {
        this.swipeStartX = event instanceof TouchEvent ? event.touches[0].clientX : event.clientX;
        this.swipeStartTime = Date.now();
    }

    onSwipeEnd(event: TouchEvent | MouseEvent, expense: any) {
        const swipeEndX = event instanceof TouchEvent ? event.changedTouches[0].clientX : event.clientX;
        const swipeTime = Date.now() - this.swipeStartTime;
        const deltaX = swipeEndX - this.swipeStartX;

        // Reset all swipes if moving
        if (Math.abs(deltaX) > 10) {
            this.currentDayGroup?.items.forEach(item => {
                if (item !== expense) (item as any).swiped = null;
            });
        }

        // Detect swipe (distance > 60px and fast enough)
        if (swipeTime < 500 && Math.abs(deltaX) > 60) {
            if (deltaX > 0) {
                // Swipe Right -> Reveal Edit
                (expense as any).swiped = 'right';
            } else {
                // Swipe Left -> Reveal Delete
                (expense as any).swiped = 'left';
            }
        } else if (Math.abs(deltaX) < 10) {
            (expense as any).swiped = null;
        }

        this.cd.detectChanges();
    }

    private showToast(title: string, message: string, type: 'success' | 'error') {
        this.snackBar.openFromComponent(ToastComponent, {
            data: { title, message, type },
            duration: 3000,
            panelClass: 'custom-toast-container',
            horizontalPosition: 'end',
            verticalPosition: 'bottom'
        });
    }
}

